import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export function SetupPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/auth/setup', {
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
      });
      await refresh();
      navigate('/');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Falha ao configurar o sistema';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center"
      style={{ backgroundColor: '#f8f7f4' }}
    >
      <div className="w-full max-w-sm px-6">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#1c1c1a' }}>
            Primeiro acesso
          </h1>
          <p className="mt-1 text-sm" style={{ color: '#6b6860' }}>
            Crie a conta de administrador para começar.
          </p>
        </div>

        <div className="rounded-lg bg-white shadow-md p-6" style={{ border: '1px solid #e2e0d8' }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="mb-1.5 block text-xs font-medium"
                style={{ color: '#6b6860' }}
              >
                Nome
              </label>
              <input
                id="name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md px-3 py-2.5 text-sm outline-none transition focus:ring-1"
                style={{
                  border: '1px solid #e2e0d8',
                  backgroundColor: '#ffffff',
                  color: '#1c1c1a',
                }}
                placeholder="Seu nome"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-xs font-medium"
                style={{ color: '#6b6860' }}
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md px-3 py-2.5 text-sm outline-none transition focus:ring-1"
                style={{
                  border: '1px solid #e2e0d8',
                  backgroundColor: '#ffffff',
                  color: '#1c1c1a',
                }}
                placeholder="voce@nobelcapital.com.br"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-xs font-medium"
                style={{ color: '#6b6860' }}
              >
                Senha
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md px-3 py-2.5 text-sm outline-none transition focus:ring-1"
                style={{
                  border: '1px solid #e2e0d8',
                  backgroundColor: '#ffffff',
                  color: '#1c1c1a',
                }}
                placeholder="mínimo 8 caracteres"
              />
            </div>

            {error && (
              <p
                className="rounded-md px-3 py-2 text-xs text-red-600"
                style={{ border: '1px solid #fca5a5', backgroundColor: '#fef2f2' }}
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-60"
              style={{ backgroundColor: '#5b5ef4' }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = '#4547d4';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = '#5b5ef4';
              }}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? 'Configurando…' : 'Criar conta'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
